import { memo, useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as PR from "../../prime-react/index";
import { chatHeadingIcon, userReceive, userSend } from "../../assets/images/index";
import "./Chat.scss";
import { chatCount, chatFileTypesConfig, cleanData, editorConfig, fileUploadLimit, getPlainText, getPlainTextVal, remainingCharacters, textSanitize, trimFileName, validationMessages } from "../../utils/reuse";
import { createOrUpdateConversations, getFileDetails, getMessagesAPI, renameTitleAndMarkAsRead } from "../../services/api";
import * as Yup from 'yup';
import { useFormik } from "formik";
import { useToast } from "../../context/ToastContext";
import MessageReceive from "./MessageReceive";
import MessageSend from "./MessageSend";
import LeftPanel from "./LeftPanel";
import MiddlePanel from "./MiddlePanel";
import useQuillTooltipPositioning from "../../utils/useQuillTooltipPositioning";
import useSenderImages from "./useSenderImages";
import moment from "moment";

const MessageList = memo(({
  messages,
  sessionGroupID,
  senderImages,
  unreadIndex,
  fetchFileDetails,
}) => (
  <>
    {messages.map((m, i) => {
      const key = m.messageID || `${m.createdAt}-${i}`;
      return m.senderID !== sessionGroupID ? (
        <MessageReceive
          key={key}
          message={m}
          index={i}
          unreadIndex={unreadIndex}
          userReceive={userReceive}
          senderImageSrc={senderImages[String(m.senderUserID)]}
          fetchFileDetails={fetchFileDetails}
        />
      ) : (
        <MessageSend
          key={key}
          message={m}
          index={i}
          userSend={userSend}
          senderImageSrc={senderImages[String(m.senderUserID)]}
          fetchFileDetails={fetchFileDetails}
        />
      );
    })}
  </>
));

const Chat = ({ type, conversations, regulatorsSGB, refetchHandler, getGroupIDHandler, ids, initialMessages }) => {
  const { showToast } = useToast();
  const dispatch = useDispatch();
  const sessionData = useSelector((state) => state.auth.sessionData);
  const multipartheaders = useMemo(() => {
    return { sessionid: sessionData.sessionId, "Content-Type": "multipart/form-data" };
  }, [sessionData.sessionId]);
  const headers = useMemo(() => {
    return { sessionid: sessionData.sessionId };
  }, [sessionData.sessionId]);
  const role = useSelector(state => state.auth.role);
  const isAnalyst = useSelector((state) => state.auth.role) === "analyst";
  const [selectRegulatorORSGB, setSelectRegulatorORSGB] = useState(null);
  const [conversationIds, setConversationIds] = useState({});
  const [isRead, setIsRead] = useState(false);
  const [messages, setMessages] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [chatLoading, setChatLoading] = useState(false);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [editData, setEditData] = useState({});
  const [editVal, setEditVal] = useState('');
  const [groupSearchInput, setGroupSearchInput] = useState("");
  const [conversationSearchInput, setConversationSearchInput] = useState("");
  const [conversationFilter, setConversationFilter] = useState("all");
  const [sortOrder, setSortOrder] = useState("default"); // 'default', 'az', 'za'
  const [conversationSortOrder, setConversationSortOrder] = useState("default"); // 'default', 'createdAt'
  const [unreadFirst, setUnreadFirst] = useState(true);
  const [initialValues, setInitialValues] = useState({ subject: '', content: '', attachments: [], });
  const inputRef = useRef(null);
  const isFirstLoad = useRef(true);
  const isSendingMessage = useRef(false);
  const currentConversationIdRef = useRef(null);
  const deferredGroupSearch = useDeferredValue(groupSearchInput);
  const deferredConversationSearch = useDeferredValue(conversationSearchInput);
  const caretPosition = useRef(0);

  const handleGroupSearchChange = useCallback((e) => {
    setGroupSearchInput(e.target.value);
  }, []);

  const handleConversationSearchChange = useCallback((e) => {
    setConversationSearchInput(e.target.value);
  }, []);

  const containerRef = useRef(null);

  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      container.scrollTop = container.scrollHeight;
    }
  }, [messages]);

  const setInputRef = useCallback((el) => {
    if (el) {
      inputRef.current = el;

      requestAnimationFrame(() => {
        // Prevent interfering with an ongoing user interaction
        if (document.activeElement !== el) {
          el.focus();
        }

        // Only try to set cursor if supported
        if (typeof el.setSelectionRange === 'function') {
          const pos = Math.min(caretPosition.current, el.value.length);
          el.setSelectionRange(pos, pos);
        }
      });
    }
  }, []);

  const messageHandler = useCallback((item) => {
    isSendingMessage.current = false;
    setIsRead(item.badge ? true : false);
    isFirstLoad.current = false;
    setConversationIds({ conversationID: item.value, groupID: item.groupID, groupName: item.groupName })
    setInitialValues({ subject: '', content: '', attachments: [], randonId: Math.random() })
  }, [])
  const groupHandler = useCallback((event) => {
    const item = event.item || event;
    if (!item?.value) return;
    isSendingMessage.current = false;
    setSelectRegulatorORSGB(null)
    getGroupIDHandler(item?.value);
    setConversationFilter("all");
    setConversationSearchInput("");
    isFirstLoad.current = true;
  }, [getGroupIDHandler]);
  const editHandler = (item) => {
    setEditData(item)
    setEditVal(item.label)
    caretPosition.current = item.label?.length;

  }
  const renameTitleAndMarkAsReadHandler = useCallback(({ groupID, conversationID }, queryParam) => {
    const getResponse = (response) => {
      if (response.result === "SUCCESS") {
        setIsRead(false)
        setEditData({})
        setEditVal('')
        refetchHandler(conversationIds,  queryParam?.isRead ? 'read' :'title');
        if(queryParam?.isTitle){
          showToast('success', 'Updated Successfully')
        }
      } else {
        const error = response.error;
        if(error?.errorMsg === 'validation error'){
           showToast('warn', 'Subject must be 100 characters or less');
        }
      }
    };
    renameTitleAndMarkAsRead(groupID, conversationID, headers, { ...queryParam, groupID}, dispatch, getResponse);
  }, [conversationIds, dispatch, headers, refetchHandler, showToast]);
  useEffect(() => {
    if (isRead) {
      renameTitleAndMarkAsReadHandler(conversationIds, { isRead: true})
    }
  }, [conversationIds, isRead, renameTitleAndMarkAsReadHandler]);
  const saveTitleHandler = useCallback(() => {
    renameTitleAndMarkAsReadHandler({ groupID: editData.groupID, conversationID: editData.value, groupName: editData.groupName }, {title :editVal, isTitle: true })
  }, [editData, editVal, renameTitleAndMarkAsReadHandler])
  
  const renderEditTemplate = useCallback(() => (
    <div className="relative w-12">
      <PR.InputText
        type="text"
        ref={setInputRef}
        value={editVal}
        className="edit-input"
        onChange={(e) => {
          caretPosition.current = e.target.selectionStart;
          setEditVal(e.target.value);
        }}
      />
      <div className="edit-label-icons">
        <span className={`check ${(!editVal || editVal === editData?.label)? 'disabled-span' : ''}`} onClick={(editVal !== editData?.label) ? saveTitleHandler : undefined}><i className="pi pi-check" /></span>
        <span className="times" onClick={editRemoveHandler}><i className="pi pi-times" /></span>
      </div>
    </div>
  ), [editData?.label, editVal, saveTitleHandler, setInputRef]);

  const getMessagesHandler = useCallback(({ groupID, conversationID }) => {
    if (conversationID !== currentConversationIdRef.current && !isSendingMessage.current) {
      setMessagesLoading(true);
    }
    currentConversationIdRef.current = conversationID;
    const getResponse = (response) => {
      if (response.result === "SUCCESS") {
        const responseData = response.data ? response.data : {};
        setMessages(
          responseData.messages?.length
            ? [...responseData.messages].sort((a, b) =>
              new Date(a.createdAt) - new Date(b.createdAt)
            )
            : []
        );
      } else {
        setMessages([])
      }
      setMessagesLoading(false);
      isSendingMessage.current = false;
    };
    getMessagesAPI(groupID, conversationID, multipartheaders, dispatch, getResponse);
  }, [dispatch, multipartheaders]);

    useEffect(() => {
    if (!conversationIds?.groupID) return;

    if (conversationIds?.conversationID === 'new') {
      setMessages([]);
      currentConversationIdRef.current = 'new';
    } else {
      if (isFirstLoad.current && initialMessages?.length) {
        // Use messages from props only once
        setMessages(initialMessages);
        currentConversationIdRef.current = conversationIds.conversationID;
      } else {
        // Fetch fresh messages
        getMessagesHandler(conversationIds);
      }
    }
  }, [conversationIds, getMessagesHandler, initialMessages]);


  useEffect(() => {
     if(ids?.groupID){
      setConversationIds(ids);
     }
  }, [ids]);

  const senderImages = useSenderImages({
    messages,
    senderIdKey: "senderUserID",
    senderImageKey: "senderUserImage",
    headers,
    dispatch,
    resetKey: conversationIds?.conversationID, // Reset cache when conversation changes
  });

  const editRemoveHandler = () => {
    setEditVal('');
    setEditData({})
    caretPosition.current = 0;
    inputRef.current?.focus();
  }

  const validationSchema = (conversationIds) =>
    Yup.object().shape({
      subject: Yup.string().when([], {
        is: () => conversationIds.conversationID === 'new',
        then: (schema) => schema.required('Subject is required').max(100, "Subject must be 100 characters or less"),
        otherwise: (schema) => schema.notRequired(),
      }),
      attachments: Yup.array()
        .nullable()
        .test("fileSize", validationMessages.sizeExceed, (value) => {
          return !value || value.every(file => file.size <= fileUploadLimit.value);
        })
        .test("fileType", validationMessages.allTypes, (value) => {
          return !value || value.every(file => chatFileTypesConfig.includes(file.type));
        }),
    });
  const createOrUpdateChatHandler = ({groupID, conversationID, groupName}, data, id) => {
    const getResponse = (response) => {
      setChatLoading(false);
      if (response.result === "SUCCESS") {
          isFirstLoad.current = false;
        if (conversationID === 'new') {
          refetchHandler({groupID, conversationID: response.data, groupName}, id ? 'create': 'existing');
          setSelectRegulatorORSGB(null);
        } else {
            setConversationIds(prev => ({ ...prev, conversationID: response.data, isRead: false }))
        }
        formik.resetForm();
      } else {
        const error = response.error; 
        setErrorMessage(error?.errorMsg || error?.summary);
        setTimeout(() => {
          setErrorMessage('');
        }, 5000);
      }
    };
    createOrUpdateConversations(`groupID=${groupID}` , data, multipartheaders, dispatch, getResponse);
  }
  const handleSubmit = (formValues) => {
    let obj = {};
    obj.content = textSanitize(formValues.content);
    if (conversationIds.conversationID !== 'new') {
      obj.conversationID = conversationIds.conversationID;
    } else {
      obj.title = formValues.subject;
    }
    const refinedObj = cleanData(obj);

    const formData = new FormData();
    if (refinedObj && Object.keys(refinedObj).length > 0) {
      formData.append('data', JSON.stringify(refinedObj));
    }
    if (formValues.attachments && formValues.attachments.length > 0) {
      formValues.attachments.forEach((file) => {
        formData.append('attachments', file);
      });
    }
    isSendingMessage.current = true;
    setChatLoading(true);
    setErrorMessage('');
    if (conversationIds.isRead) {
      renameTitleAndMarkAsReadHandler(conversationIds, { isRead: true})
    }
    createOrUpdateChatHandler(conversationIds, formData, selectRegulatorORSGB?.id)
   
  }
  const formik = useFormik({
    initialValues,
    validationSchema: validationSchema(conversationIds),
    onSubmit: handleSubmit,
    enableReinitialize: true
  });
  const handleDeleteAttachment = (index) => {
    const updatedAttachments = [...formik.values.attachments];
    updatedAttachments.splice(index, 1);
    formik.setFieldValue('attachments', updatedAttachments);
  };
  const fetchFileDetails = (key, fileName) => {
    const convertBlobToPDFAndDownload = async (pdfData, fileName) => {
      try {
        const url = URL.createObjectURL(pdfData);

        const link = document.createElement("a"); //download link
        link.href = url;
        link.setAttribute("download", fileName || "file.pdf");

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } catch (error) {
        showToast(error.severity, error?.errorMsg || error?.summary);
      }
    };
    getFileDetails(key, headers, dispatch, (response) => {
      if (response.result === "SUCCESS") {
        const fileData = response.data;
        convertBlobToPDFAndDownload(fileData, fileName);
      } else {
        const error = response?.error;
        showToast(error.severity, error?.errorMsg || error?.summary);
      }
    });
  };
  const unreadIndex = messages.findIndex(m => !m.readAt);

  const groupsData = useMemo(() => {
    const existingGroups = (conversations || []).map((group) => ({
      id: group.groupID,
      name: group.groupName,
      unreadConversationCount: group.unreadConversationCount || 0,
      conversations: group.conversations || [],
      hasExistingThread: true,
    }));

    const extraGroups = type === "analyst"
      ? (regulatorsSGB || [])
          .filter((group) => !existingGroups.some((existing) => existing.id === group.id))
          .map((group) => ({
            id: group.id,
            name: group.name,
            unreadConversationCount: 0,
            conversations: [],
            hasExistingThread: false,
          }))
      : [];

    const mergedGroups = [...existingGroups, ...extraGroups];

    if (
      selectRegulatorORSGB?.id &&
      !mergedGroups.some((group) => group.id === selectRegulatorORSGB.id)
    ) {
      mergedGroups.unshift({
        id: selectRegulatorORSGB.id,
        name: selectRegulatorORSGB.name,
        unreadConversationCount: 0,
        conversations: [],
        hasExistingThread: false,
      });
    }

    return mergedGroups;
  }, [conversations, regulatorsSGB, selectRegulatorORSGB, type]);

  const filteredGroups = useMemo(() => {
    const search = deferredGroupSearch.trim().toLowerCase();
    const baseGroups = !search
      ? groupsData
      : groupsData.filter((group) => group.name?.toLowerCase().includes(search));

    const compareNames = (a, b) => {
      if (sortOrder === "az") {
        return a.name.localeCompare(b.name);
      }
      if (sortOrder === "za") {
        return b.name.localeCompare(a.name);
      }
      return 0;
    };

    let result = [...baseGroups];

    if (unreadFirst) {
      const unread = result.filter((g) => g.unreadConversationCount > 0);
      const read = result.filter((g) => !(g.unreadConversationCount > 0));

      unread.sort(compareNames);
      read.sort(compareNames);

      result = [...unread, ...read];
    } else if (sortOrder !== "default") {
      result.sort(compareNames);
    }

    return result;
  }, [deferredGroupSearch, groupsData, sortOrder, unreadFirst]);

  const regulatorChangeHandler = useCallback((regulator) => {
    isSendingMessage.current = false;
    setSelectRegulatorORSGB(regulator);

    if (regulator) {
      setIsRead(false);
      setConversationIds({ conversationID: "new", groupID: regulator?.id, groupName: regulator.name });
      return;
    }

    const fallbackGroup = groupsData.find((group) => group.id !== selectRegulatorORSGB?.id);
    if (fallbackGroup?.id) {
      getGroupIDHandler(fallbackGroup.id);
    } else {
      setConversationIds({});
    }
  }, [getGroupIDHandler, groupsData, selectRegulatorORSGB?.id]);

  const activeGroup = useMemo(() => {
    if (conversationIds?.groupID) {
      return groupsData.find((group) => group.id === conversationIds.groupID);
    }
    return filteredGroups[0] || groupsData[0] || null;
  }, [conversationIds?.groupID, filteredGroups, groupsData]);

  const allConversationsForActiveGroup = useMemo(() => {
    if (!activeGroup) return [];
    const conversationsForGroup = (activeGroup.conversations || []).map((conversation) => ({
      ...conversation,
      label: getPlainTextVal(conversation.conversationTitle),
      value: conversation.conversationID,
      groupID: activeGroup.id,
      groupName: activeGroup.name,
      badge: conversation.unreadMessageCount ? conversation.unreadMessageCount : "",
    }));

    if (
      conversationIds?.conversationID === "new" &&
      conversationIds?.groupID === activeGroup.id &&
      !conversationsForGroup.some((conversation) => conversation.value === "new")
    ) {
      conversationsForGroup.unshift({
        label: "Untitled",
        value: "new",
        groupID: activeGroup.id,
        groupName: activeGroup.name,
        badge: "",
        messages: [],
        isTemporary: true,
      });
    }

    return conversationsForGroup;
  }, [activeGroup, conversationIds?.conversationID, conversationIds?.groupID]);

  const filteredConversations = useMemo(() => {
    const search = deferredConversationSearch.trim().toLowerCase();
    const filtered = allConversationsForActiveGroup.filter((conversation) => {
      const matchesFilter = conversationFilter === "unread"
        ? Boolean(conversation.unreadMessageCount)
        : true;
      const matchesSearch = search
        ? conversation.label?.toLowerCase().includes(search)
        : true;

      return matchesFilter && matchesSearch;
    });

    if (conversationSortOrder === "createdAt") {
      return [...filtered].sort((a, b) => {
        if (a.isTemporary || a.value === "new") return -1;
        if (b.isTemporary || b.value === "new") return 1;

        const dateA = a.createdAt ? new Date(a.createdAt) : (a.lastUpdatedAt ? new Date(a.lastUpdatedAt) : 0);
        const dateB = b.createdAt ? new Date(b.createdAt) : (b.lastUpdatedAt ? new Date(b.lastUpdatedAt) : 0);
        return dateB - dateA; // Newest first
      });
    }

    return filtered;
  }, [allConversationsForActiveGroup, conversationFilter, deferredConversationSearch, conversationSortOrder]);

  // Pagination for conversation list (frontend)
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const lastPagedConversationIdRef = useRef(null);

  useEffect(() => {
    // reset to first page when filter/search/group/sort changes
    setCurrentPage(1);
  }, [conversationFilter, conversationSearchInput, activeGroup?.id, conversationSortOrder]);

  useEffect(() => {
    if (!conversationIds?.conversationID) {
      lastPagedConversationIdRef.current = null;
      return;
    }
    if (filteredConversations.length) {
      if (lastPagedConversationIdRef.current !== conversationIds.conversationID) {
        const index = filteredConversations.findIndex(
          (c) => c.value === conversationIds.conversationID
        );
        if (index !== -1) {
          const page = Math.floor(index / itemsPerPage) + 1;
          setCurrentPage(page);
          lastPagedConversationIdRef.current = conversationIds.conversationID;
        }
      }
    }
  }, [conversationIds?.conversationID, filteredConversations]);

  const totalConversations = filteredConversations.length;
  const totalPages = Math.max(1, Math.ceil(totalConversations / itemsPerPage));
  const paginatedConversations = filteredConversations.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  const startIndex = totalConversations === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const endIndex = Math.min(currentPage * itemsPerPage, totalConversations);
  const pageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1);

  const startNewConversation = useCallback((group) => {
    if (!group) return;
    isSendingMessage.current = true;

    if (type === "analyst" && !group.hasExistingThread) {
      regulatorChangeHandler({ id: group.id, name: group.name });
      return;
    }

    setIsRead(false);
    setCurrentPage(1);

    isFirstLoad.current = false;
    // keep selected group so it appears in the groups list when creating a temporary thread
    setSelectRegulatorORSGB(group);
    setConversationIds({
      conversationID: "new",
      groupID: group.id,
      groupName: group.name,
    });
    setInitialValues({ subject: "", content: "", attachments: [], randonId: Math.random() });
  }, [regulatorChangeHandler, type]);

  const groupInitials = useCallback((name = "") => {
    return name
      .split(" ")
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase())
      .join("");
  }, []);

  const getConversationTime = useCallback((conversation) => {
    const stamp = conversation?.lastUpdatedAt;

    if (!stamp) return "";

    const m = moment(stamp);
    if (!m.isValid()) return "";

    return m.isSame(moment(), "day")
      ? m.format("HH:mm")
      : m.format("DD MMM");
  }, []);

  useQuillTooltipPositioning();

  return (
    <>
      <section className="chat-section">
        <div className={`chat-shell ${!isAnalyst ? "chat-shell--compact" : ""}`}>
          <LeftPanel
            isAnalyst={isAnalyst}
            filteredGroups={filteredGroups}
            groupSearchInput={groupSearchInput}
            onGroupSearchChange={handleGroupSearchChange}
            groupHandler={groupHandler}
            regulatorChangeHandler={regulatorChangeHandler}
            conversationIds={conversationIds}
            groupInitials={groupInitials}
            sortOrder={sortOrder}
            setSortOrder={setSortOrder}
            unreadFirst={unreadFirst}
            setUnreadFirst={setUnreadFirst}
          />

          <MiddlePanel
            isAnalyst={isAnalyst}
            activeGroup={activeGroup}
            groupsData={groupsData}
            sessionData={sessionData}
            conversationSearchInput={conversationSearchInput}
            onConversationSearchChange={handleConversationSearchChange}
            conversationFilter={conversationFilter}
            setConversationFilter={setConversationFilter}
            startNewConversation={startNewConversation}
            paginatedConversations={paginatedConversations}
            conversationIds={conversationIds}
            editData={editData}
            editHandler={editHandler}
            renderEditTemplate={renderEditTemplate}
            messageHandler={messageHandler}
            filteredConversations={filteredConversations}
            getConversationTime={getConversationTime}
            totalConversations={totalConversations}
            startIndex={startIndex}
            endIndex={endIndex}
            itemsPerPage={itemsPerPage}
            currentPage={currentPage}
            totalPages={totalPages}
            pageNumbers={pageNumbers}
            setCurrentPage={setCurrentPage}
            groupInitials={groupInitials}
            role={role}
            conversationSortOrder={conversationSortOrder}
            setConversationSortOrder={setConversationSortOrder}
          />

          <div className="chat-main-panel">
            <div className="chat-conversation-header">
              <div className="chat-conversation-title">
                <PR.Image src={chatHeadingIcon} alt="Chat" className="chat-image" />
                <h1>Conversation with {isAnalyst ? (conversationIds?.groupName || activeGroup?.name) : "IBIA"}</h1>
              </div>
            </div>
            <div className="chat-details">
              {messagesLoading ? (
                <div className="chat-loading-section">
                  <div className="card flex align-items-center justify-content-center">
                    <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem' }}></i>
                    <p style={{ marginTop: '1.2rem' }}>Loading messages...</p>
                  </div>
                </div>
              ) : (
                <>
                  {messages?.length > 0 &&
                    <div className="chat-messages" ref={containerRef}>
                      <MessageList
                        messages={messages}
                        sessionGroupID={sessionData.groupID}
                        senderImages={senderImages}
                        unreadIndex={unreadIndex}
                        fetchFileDetails={fetchFileDetails}
                      />
                    </div>
                  }
                  {(conversationIds.conversationID && messages?.length === 0) && (
                    <div className="chat-loading-section">
                      <div className="card">
                        <p>No new messages..</p>
                      </div>
                    </div>
                  )}
                  {(!conversationIds.conversationID) &&
                    <div className="chat-loading-section">
                      <div className="card">
                        <div className="small-box">
                          <div className="flex align-items-center">
                            <div>
                              <PR.Skeleton className="bars"></PR.Skeleton>
                              <PR.Skeleton className="bars custom-width"></PR.Skeleton>
                            </div>
                          </div>
                        </div>
                        <div className="big-box">
                          <div className="flex align-items-center justify-content-end">
                            <div>
                              <PR.Skeleton className="bars"></PR.Skeleton>
                              <PR.Skeleton className="bars"></PR.Skeleton>
                              <PR.Skeleton className="bars custom-width"></PR.Skeleton>
                            </div>
                          </div>
                        </div>
                        <div className="small-box box-width">
                          <div className="flex align-items-center">
                            <div>
                              <PR.Skeleton className="bars"></PR.Skeleton>
                              <PR.Skeleton className="bars"></PR.Skeleton>
                              <PR.Skeleton className="bars custom-width"></PR.Skeleton>
                            </div>
                          </div>
                        </div>
                        <p>Select a conversation to start messaging.</p>
                      </div>
                    </div>
                  }
                </>
              )}
              {(conversationIds.conversationID) &&
                <div className="chat-form">

                  <form autoComplete="off" onSubmit={formik.handleSubmit}>
                    {conversationIds.conversationID === 'new' &&
                      <div className="mb-3 chat-subject-field">
                        <PR.InputText
                          id="subject"
                          type="text"
                          placeholder="Subject"
                          value={formik.values.subject}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          className={`input-field ${formik.touched.subject && formik.errors.subject ? "p-invalid" : ""}`}
                        />
                        {formik.touched.subject && formik.errors.subject && (
                          <div className="error-message">{formik.errors.subject}</div>
                        )}
                      </div>
                    }
                    <div className="editor-wrapper">
                      <div className="p-editor-container">
                        <PR.Editor
                          placeholder="Message to send..."
                          name="content"
                          value={formik.values.content}
                          onTextChange={(e) => {
                            const htmlValue = e.htmlValue;
                            if (getPlainText(htmlValue) === 0 && htmlValue !== null) {
                              setErrorMessage('Enter valid text');
                            } else if (getPlainText(htmlValue) > chatCount) {
                              formik.setFieldValue('content', htmlValue);
                              setErrorMessage(`Message should be ${chatCount} characters or less.`);
                            } else {
                              formik.setFieldValue('content', htmlValue);
                              setErrorMessage("");
                            }
                          }}
                          modules={editorConfig.modules}
                          formats={editorConfig.formats}
                          className="chat-editor"
                        />
                        <div className="chat-editor-footer">
                          <PR.Button label="Send Message" type="submit" 
                          className={`chat-button send ${
                              role === 'regulator'
                              ? 'regulator'
                              : role === 'member'
                              ? 'member'
                              : role === 'analyst'
                              ? 'analyst'
                              : role === 'managedmember'
                              ? 'member'
                              : role === 'governingbody'
                              ? 'regulator'
                              : ''
                          }`}
                          disabled={!(formik.values.content?.trim() || formik.values.attachments?.length > 0)  || errorMessage || chatLoading} />
                        </div>
                      </div>
                    </div>
                    <div className="align-items-start justify-content-between mt-2">
                      <div className="descriptionGrid descriptioncharLimit">
                        <span style={{ textAlign: "left" }}>
                          {errorMessage && <div className="error-message">{errorMessage}</div>}
                        </span>
                        {remainingCharacters(chatCount, formik.values.content)}
                      </div>
                    </div>
                    <div className="choose-file-upload flex align-items-center">
                      <PR.FileUpload
                        auto
                        mode="advanced"
                        name="attachments"
                        accept={chatFileTypesConfig?.toString()}
                        chooseLabel="Choose File"
                        uploadLabel="Upload"
                        emptyTemplate={null}
                        itemTemplate={null}
                        customUpload
                        onSelect={(event) => {
                          const selectedFiles = event.files; // Get the selected files
                          formik.setFieldTouched("attachments", true); // Mark field as touched
                          formik.setFieldValue("attachments", selectedFiles); // Update Formik's state
                        }}
                      />
                      <span className="ml-2">
                        <span className="block">(PDF, DOC, DOCX, XLS, XLSX, CSV, PNG, JPG or JPEG)</span>
                        {validationMessages.sizeExceed}.
                      </span>

                    </div>
                    {formik.values.attachments?.length > 0 && (
                      <div className="uploaded-file-container flex flex-column gap-2 mt-1">
                        {formik.values.attachments?.map((attachment, index) => (
                          <div className="uploaded-files" key={index}>
                            {trimFileName(attachment.name)}
                            <i className="pi pi-trash" onClick={() => handleDeleteAttachment(index)} />
                          </div>
                        ))}
                      </div>
                    )}
                    {formik.errors.attachments && (formik.touched.attachments || formik.submitCount > 0) && (
                      <div className="error-message">
                        {Array.isArray(formik.errors.attachments) ? (
                          Array.from(new Set(formik.errors?.attachments)).map((error, index) => (
                            <div key={index}>{error}</div>
                          ))
                        ) : (
                          <div>{formik.errors.attachments}</div>
                        )}
                      </div>
                    )}
                  </form>
                </div>
              }
            </div>
          </div>
        </div>
      </section >
    </>
  );
};

export default Chat;
